import type { MessageContentPanelProps } from './MessageContentPanel';
import type { GlobalMessageBoxNotification } from '../../../../amplienceTypes/schemas/imported/global-message-box-notification-schema';
import type { HeaderOpenModalProps } from '../../core/layouts/MasterLayout';
import type { ReactElement } from 'react';

import dynamic from 'next/dynamic';
import { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { useMessageBoxNotification } from './useMessageBoxNotification';
import EmailIcon from '../../core/icons/EmailIcon';
import { media } from '../../core/styles';
import { Typography } from '../../core/typography/Typography';
import { HeaderButton } from '../HeaderStyle';
import { useMobileTopLevelNavigationOwnsOverlays } from '../topLevelNavigation/hooks/useTopLevelNavigationEnabled';

const MessageContentPanel = dynamic<MessageContentPanelProps>(
  () => import('./MessageContentPanel').then(mod => mod.MessageContentPanel),
  { ssr: false }
);

const S = {
  MessageBoxNotification: styled.div`
    ${({ theme }) => css`
      display: flex;
      align-items: center;
      gap: ${theme.size[2]};
      position: relative;
      border: none;
    `}
  `,
  MessageBoxButton: styled.button<{ $openedModal: boolean }>`
    ${({ theme, $openedModal }) => css`
      ${HeaderButton}

      border: 0;
      cursor: pointer;
      color: ${theme.colors.content.component.header.color};

      &:hover,
      &:focus {
        path {
          fill: ${theme.colors.content.component.header.hover};
        }

        p {
          color: ${theme.colors.content.component.header.hover};
        }
      }

      padding: ${theme.padding.none} ${theme.padding['2xs']} ${theme.padding.none}
        ${theme.padding.sm};

      @media ${media(theme).greaterThan('sm')} {
        padding: ${theme.padding.none};
      }

      ${$openedModal &&
      css`
        @media ${media(theme).greaterThan('sm')} {
          &::before {
            content: '';
            border: 8px solid transparent;
            border-bottom-color: ${theme.colors.content.component.header.color};
            position: absolute;
            bottom: 2px;
          }
        }
      `}
    `}
  `,
  IconWrapper: styled.span`
    ${({ theme }) => css`
      display: inline-block;
      width: ${theme.size[4]};
      height: ${theme.size[4]};

      g {
        fill: ${theme.colors.content.primary};
      }

      @media ${media(theme).greaterThan('sm')} {
        padding-bottom: 0;
      }
    `}
  `,
  MessageCount: styled(Typography)`
    ${({ theme }) => css`
      color: ${theme.colors.content.component.header.color};
      font-size: 14px;
      font-weight: 700;
    `}
  `,
};

export const MessageBoxNotification = ({
  openedModal,
  setOpenedModal,
  ...content
}: HeaderOpenModalProps & GlobalMessageBoxNotification): ReactElement => {
  const { messageCount, title, text, link, buttonLabel, markAsRead } =
    useMessageBoxNotification(content);
  // Below `lg` the mobile TLN drawer renders the panel. Both bells (top and side navigation) are
  // hidden there, so neither renders its own copy; they keep counting and syncing the read state.
  const ownsPanel = useMobileTopLevelNavigationOwnsOverlays() === false;

  const openModal = useCallback(() => {
    setOpenedModal(openedModal === 'message-box-notification' ? null : 'message-box-notification');
    markAsRead();
  }, [markAsRead, openedModal, setOpenedModal]);

  return (
    <S.MessageBoxNotification onClick={openModal} data-testid="notification-message-icon">
      <S.MessageBoxButton
        $openedModal={openedModal === 'message-box-notification'}
        type="button"
        aria-haspopup="true"
        aria-label={`You have ${messageCount === 0 ? 'no' : messageCount} ${
          messageCount === 1 ? 'notification' : 'notifications'
        }`}
      >
        <S.IconWrapper>
          <EmailIcon />
        </S.IconWrapper>
        {messageCount > 0 && (
          <S.MessageCount data-testid="notification-message-count">{messageCount}</S.MessageCount>
        )}
      </S.MessageBoxButton>
      {openedModal === 'message-box-notification' && ownsPanel && (
        <MessageContentPanel
          isActive={openedModal === 'message-box-notification'}
          title={title}
          text={text}
          link={link}
          buttonLabel={buttonLabel}
          onClose={() => setOpenedModal(null)}
        />
      )}
    </S.MessageBoxNotification>
  );
};
